import type { Config } from 'tailwindcss';

import plugin from 'tailwindcss/plugin';

export const colors = {
  default: '#EB4646',
  mmhmm: '#4339E1',
  at: '#EB4646',
  carrot: '#F35B09',
  'carrot-dark': '#2D2D2D',
  sora: '#0A8ABA',
  white: '#FFFFFF',
  dark: '#192836',
  sky: '#252F3A',
  airtime: '#1D8C98',
  partners: '#f3f4f5',
  navy: '#192836',
  bentoville1: '#ea6951',
  bentoville2: '#1D1A15',
} as const;

const config: Config = {
  mode: 'jit', // Enable JIT mode
  content: ['./src/**/*.{js,ts,jsx,tsx,mdx}'],
  safelist: [
    'hover:text-mmhmm',
    'hover:text-at',
    'hover:text-carrot',
    'hover:text-carrot-dark',
    'hover:text-sora',
    'hover:text-white',
    'hover:text-dark',
    'hover:text-sky',
    'hover:text-airtime',
    'hover:text-partners',
    'hover:text-navy',
    'animate-[bounce_1.5s_ease-in-out_infinite]',
  ],
  theme: {
    extend: {
      colors,
      fill: colors,
      fontFamily: {
        soehne: ['var(--font-soehne)'],
        heldane: ['var(--font-heldane)'],
        breit: ['var(--font-breit)'],
      },
      fontWeight: {
        leicht: '300',
        buch: '400',
        kraftig: '500',
        halbfett: '600',
        fett: '800',
      },
      fontSize: {
        /* Explanation here: https://medium.com/@bogdanfromkyiv/dynamic-font-size-using-only-css3-757ea901d0fe
        
        Formula:
        font-size: clamp(16px, calc(20px + (36–20) * (100vw - 768px)/(1920–768)), 48px);

        3 arguments: clamp(first, second, third)
        first: min font-size at smallest size
        second: 20-36 are the min & max size between min & max breakpoint
        third: max font-size at biggest size
        */
        homeIntro: 'clamp(24px, calc(24px + (48 - 24) * (100vw - 375px)/(1920 - 375)), 48px)',
        homeCompanies: 'clamp(20px, calc(20px + (44 - 20) * (100vw - 375px)/(1920 - 375)), 44px)',
        homeEyebrow: 'clamp(16px, calc(16px + (28 - 16) * (100vw - 375px)/(1920 - 375)), 28px)',
        homeStyle3: 'clamp(38px, calc(38px + (76 - 38) * (100vw - 375px)/(1920 - 375)), 76px)',
        homeStyle4: 'clamp(74px, calc(74px + (140 - 74) * (100vw - 375px)/(1920 - 375)), 140px)',
        homeSpotlightTitle: 'clamp(56px, calc(56px + (140 - 56) * (100vw - 375px)/(1920 - 375)), 140px)',
        homeSpotlightDesc: 'clamp(32px, calc(32px + (42 - 32) * (100vw - 375px)/(1920 - 375)), 42px)',
        homeStyle7: 'clamp(38px, calc(38px + (42 - 38) * (100vw - 375px)/(1920 - 375)), 42px)',
        homeLetsTalk: 'clamp(38px, calc(38px + (92 - 38) * (100vw - 375px)/(1920 - 375)), 92px)',

        // hero title - biggest size in the hero
        heroFirstLine: 'clamp(38px, calc(38px + (72 - 38) * (100vw - 375px)/(1400 - 375)), 72px)',
        heroSecondLine: 'clamp(34px, calc(34px + (64 - 34) * (100vw - 375px)/(1400 - 375)), 64px)',
        // case study huge - project title
        spotlightHuge: 'clamp(720px, calc(720px + (700 - 720) * (100vw - 375px)/(1920 - 375)), 700px)',
        // case study title - 2nd size text
        spotlightHeading: 'clamp(50px, calc(50px + (160 - 50) * (100vw - 375px)/(1920 - 375)), 160px)',
        // case study description - copy & 3rd size text
        spotlightDescription: 'clamp(29px, calc(29px + (42 - 29) * (100vw - 375px)/(1920 - 375)), 42px)',
        // eyebrow
        eyebrowHeading: 'clamp(20px, calc(20px + (30 - 20) * (100vw - 375px)/(1920 - 375)), 30px)',
        caseStudyHeading: 'clamp(64px, calc(64px + (200 - 64) * (100vw - 375px)/(1920 - 375)), 200px)',

        /* Services: fluid from 767px to 1328px viewport so large text scales down */
        serviceHeroStatement: 'clamp(44px, calc(44px + (144 - 44) * (100vw - 767px) / (1328 - 767)), 144px)',
        serviceHeroEyebrow: 'clamp(18px, calc(18px + (28 - 18) * (100vw - 767px) / (1328 - 767)), 28px)',
        serviceBlurbTitle: 'clamp(24px, calc(24px + (144 - 24) * (100vw - 767px) / (1328 - 767)), 48px)',
        serviceBlurbBody: 'clamp(17px, calc(17px + (24 - 17) * (100vw - 767px) / (1328 - 767)), 24px)',
        serviceProof: 'clamp(24px, calc(24px + (72 - 24) * (100vw - 767px) / (1328 - 767)), 72px)',
        serviceBodyLarge: 'clamp(28px, calc(28px + (48 - 28) * (100vw - 767px) / (1328 - 767)), 48px)',
        serviceBody: 'clamp(17px, calc(17px + (24 - 17) * (100vw - 767px) / (1328 - 767)), 24px)',
        /* 70% of serviceBody (e.g. disclaimer text) */
        serviceBodySmall: 'clamp(12px, calc(12px + (17 - 12) * (100vw - 767px) / (1328 - 767)), 17px)',
        serviceQuote: 'clamp(18px, calc(18px + (24 - 18) * (100vw - 767px) / (1328 - 767)), 24px)',
        serviceCaption: 'clamp(16px, calc(16px + (20 - 16) * (100vw - 767px) / (1328 - 767)), 20px)',
        serviceThinTitle: 'clamp(24px, calc(24px + (40 - 24) * (100vw - 767px) / (1328 - 767)), 40px)',
        serviceCTAText: 'clamp(18px, calc(18px + (28 - 18) * (100vw - 767px) / (1328 - 767)), 28px)',
        serviceCardTitle: 'clamp(18px, calc(18px + (24 - 18) * (100vw - 767px) / (1328 - 767)), 24px)',
        serviceCardBody: 'clamp(16px, calc(17px + (16 - 17) * (100vw - 767px) / (1328 - 767)), 17px)',
        serviceSectionHeading: 'clamp(17px, calc(17px + (24 - 17) * (100vw - 767px) / (1328 - 767)), 24px)',
      },
      letterSpacing: {
        /* Services hero statement: scales with viewport 767→1328 */
        serviceHeroStatement: 'clamp(-1px, calc(-1px + (-2.88 - -1) * (100vw - 767px) / (1328 - 767)), -2.88px)',
      },
      screens: {
        /* we use these for static pages */
        'mobile-only': { max: '766px' },
        tablet: { raw: '(min-width: 767px)' },
        desktop: { raw: '(min-width: 1024px) and (orientation: landscape)' },
        desktopXL: { raw: '(min-width: 1440px) and (orientation: landscape)' },
        desktop4k: { raw: '(min-width: 1921px) and (orientation: landscape)' },
      },
      keyframes: {
        'show-in': {
          '0%': { opacity: '0' },
          '100%': { opacity: '1' },
        },
        'first-line-animation': {
          '0%': { opacity: '0', transform: 'translateY(100%)' },
          '100%': { opacity: '1', transform: 'translateY(0%)' },
        },
      },
      animation: {
        'fade-in': 'show-in 500ms cubic-bezier(0.2, 0.8, 0.2, 1) infinite',
        'first-line': 'first-line-animation 500ms ease-in-out forwards',
        'scroll-driven': 'translate3d(0, var(--y, 0), 0)',
      },
      spacing: {
        'h-margin-mobile': 'clamp(25px, calc(25px + (60 - 25) * (100vw - 375px)/(768 - 375)), 60px)',
        'h-margin-tablet': 'clamp(60px, calc(60px + (80 - 60) * (100vw - 768px)/(1280 - 768)), 80px)',
        'h-margin-desktop': 'clamp(80px, calc(80px + (215 - 80) * (100vw - 1280px)/(1920 - 1280)), 215px)',
        'h-margin-XLdesktop': '215px', // Fixed at largest size
      },
      height: {
        screen: 'var(--dvh)',
        'screen-100': 'var(--dvh-100)',
        'screen-200': 'var(--dvh-200)',
      },
    },
  },
  plugins: [
    ...(process.env.NODE_ENV === 'development' ? [require('tailwindcss-debug-screens')] : []),
    plugin(function ({ addUtilities }) {
      addUtilities({
        '.ligatures-normal': {
          fontVariantLigatures: 'normal',
        },
        '.ligatures-none': {
          fontVariantLigatures: 'none',
        },
        '.ligatures-common': {
          fontVariantLigatures: 'common-ligatures',
        },
        '.ligatures-no-common': {
          fontVariantLigatures: 'no-common-ligatures',
        },
        '.ligatures-discretionary': {
          fontVariantLigatures: 'discretionary-ligatures',
        },
        '.ligatures-no-discretionary': {
          fontVariantLigatures: 'no-discretionary-ligatures',
        },
        '.ligatures-historical': {
          fontVariantLigatures: 'historical-ligatures',
        },
        '.ligatures-no-historical': {
          fontVariantLigatures: 'no-historical-ligatures',
        },
        '.ligatures-contextual': {
          fontVariantLigatures: 'contextual',
        },
        '.ligatures-no-contextual': {
          fontVariantLigatures: 'no-contextual',
        },
      });
    }),
  ],
};
export default config;
